/**
 * The contract for every event this app pushes to the GTM `window.dataLayer`.
 *
 * This file is the single source of truth. If an event is not declared here it
 * cannot be pushed through `AnalyticsService`, and if a payload does not match
 * its declaration the analytics specs fail — which fails the build before a
 * deploy can ship broken GA4 reporting.
 *
 * When you add or change tracking:
 *   1. Declare the event here (or amend its field list).
 *   2. Update the fixture in the matching `*.analytics.spec.ts`.
 *   3. Run `npm run test:analytics`.
 */

export type AnalyticsFieldType =
  | 'string'
  | 'number'
  | 'boolean'
  | 'object'
  | 'object[]'
  | 'string[]';

export interface AnalyticsFieldSpec {
  type: AnalyticsFieldType;
  /** Field must be present. Absent (not merely null) fails validation. */
  required?: boolean;
  /** Field may hold `null` in addition to its declared type. */
  nullable?: boolean;
  /** Closed set of permitted values. */
  oneOf?: Array<string | number | boolean>;
  /** For 'object' / 'object[]', the shape each entry must satisfy. */
  shape?: AnalyticsFieldSpecMap;
}

export interface AnalyticsFieldSpecMap {
  [field: string]: AnalyticsFieldSpec;
}

export interface AnalyticsEventSpec {
  /** Human-readable note on when this event fires — shown in failure output. */
  description: string;
  fields: AnalyticsFieldSpecMap;
  /** Reject payload keys that are not declared. Defaults to false. */
  strictKeys?: boolean;
  /**
   * Alternative shapes the same event name may take. The payload is valid if it
   * matches `fields` or any variant. This exists because the bus and flights
   * funnels both emit `purchase` with entirely different payloads, and renaming
   * either would break the live GTM tags that consume them.
   */
  variants?: Array<{
    name: string;
    fields: AnalyticsFieldSpecMap;
    strictKeys?: boolean;
  }>;
}

export interface AnalyticsEventSchema {
  [eventName: string]: AnalyticsEventSpec;
}

const TRIP_TYPES = ['One Way', 'Return'];
const TRIP_LEGS = ['Outbound', 'Return'];
const USER_TYPES = ['guest', 'logged_in'];

/** Fields shared by every GA4 funnel event, from search through purchase. */
const JOURNEY_FIELDS: AnalyticsFieldSpecMap = {
  route_from: { type: 'string', required: true },
  route_to: { type: 'string', required: true },
  travel_date: { type: 'string', required: true, nullable: true },
  return_date: { type: 'string', required: true, nullable: true },
  trip_type: { type: 'string', required: true, oneOf: TRIP_TYPES }
};

/** Identity fields GA4 segments on. */
const USER_FIELDS: AnalyticsFieldSpecMap = {
  user_logged_in: { type: 'boolean', required: true },
  user_type: { type: 'string', required: true, oneOf: USER_TYPES }
};

/** The money fields. These drive revenue reporting, so all are required. */
const PRICING_FIELDS: AnalyticsFieldSpecMap = {
  base_fare: { type: 'number', required: true },
  convenience_fee: { type: 'number', required: true },
  discount: { type: 'number', required: true },
  final_price: { type: 'number', required: true }
};

/** Shape of one bus in an item list (`view_item_list`, `payment_initiated`). */
const LIST_ITEM_SHAPE: AnalyticsFieldSpecMap = {
  item_id: { type: 'string', required: true },
  item_name: { type: 'string', required: true },
  bus_operator: { type: 'string', required: true },
  departure_time: { type: 'string', required: true, nullable: true },
  base_fare: { type: 'number', required: true },
  convenience_fee: { type: 'number', required: true },
  discount: { type: 'number', required: true },
  final_price: { type: 'number', required: true },
  seats_available: { type: 'number', required: true },
  seat_types_available: { type: 'string[]', required: true },
  route: { type: 'string', required: true }
};

/** Shape of the single bus a user committed to. */
const SELECTED_ITEM_SHAPE: AnalyticsFieldSpecMap = {
  item_id: { type: 'string', required: true, nullable: true },
  item_name: { type: 'string', required: true },
  bus_operator: { type: 'string', required: true },
  seat_type: { type: 'string', required: true, nullable: true },
  quantity: { type: 'number', required: true },
  base_fare: { type: 'number', required: true },
  convenience_fee: { type: 'number', required: true },
  discount: { type: 'number', required: true },
  final_price: { type: 'number', required: true },
  route: { type: 'string', required: true },
  boarding_point: { type: 'string', required: true, nullable: true },
  dropping_point: { type: 'string', required: true, nullable: true }
};

const PASSENGER_INFO_SHAPE: AnalyticsFieldSpecMap = {
  passenger_index: { type: 'number', required: true },
  gender: { type: 'string', required: true, nullable: true },
  location: { type: 'string', required: true, nullable: true },
  age: { type: 'number', required: true, nullable: true }
};

/** Payload of the legacy `{event, time, data:{cityFrom, cityTo}}` route tags. */
const LEGACY_ROUTE_TAG: AnalyticsEventSpec['fields'] = {
  time: { type: 'number', required: true },
  data: {
    type: 'object',
    required: true,
    shape: {
      cityFrom: { type: 'string', required: true },
      cityTo: { type: 'string', required: true }
    }
  }
};

/** Payload of the legacy `{event, time, data:{fromCity,...}}` inventory tags. */
const LEGACY_INVENTORY_TAG: AnalyticsEventSpec['fields'] = {
  time: { type: 'number', required: true },
  data: {
    type: 'object',
    required: true,
    shape: {
      fromCity: { type: 'string', required: true },
      toCity: { type: 'string', required: true },
      operator: { type: 'string', required: true },
      departure: { type: 'string', required: true, nullable: true }
    }
  }
};

export const ANALYTICS_EVENT_SCHEMA: AnalyticsEventSchema = {
  // ---------------------------------------------------------------- discovery

  page_load: {
    description: 'Every Angular router NavigationEnd.',
    strictKeys: true,
    fields: {
      time: { type: 'number', required: true },
      pageName: { type: 'string', required: true }
    }
  },

  search_route: {
    description: 'User submits the home-page route search form.',
    strictKeys: true,
    fields: {
      ...JOURNEY_FIELDS,
      ...USER_FIELDS
    }
  },

  popular_route: {
    description: 'User clicks a pre-selected popular bus route.',
    strictKeys: true,
    fields: LEGACY_ROUTE_TAG
  },

  popular_flight_route: {
    description: 'User clicks a pre-selected popular flight route.',
    strictKeys: true,
    fields: LEGACY_ROUTE_TAG
  },

  inventory_search: {
    description: 'An inventory request is issued for a route/date pair.',
    strictKeys: true,
    fields: {
      data: {
        type: 'object',
        required: true,
        shape: {
          fromCity: { type: 'string', required: true },
          toCity: { type: 'string', required: true },
          departureDate: { type: 'string', required: true, nullable: true },
          returnDate: { type: 'string', required: true, nullable: true }
        }
      }
    }
  },

  view_item_list: {
    description: 'Search results render (or come back empty) on the tickets page.',
    fields: {
      ...JOURNEY_FIELDS,
      ...USER_FIELDS,
      item_list_name: { type: 'string', required: true },
      trip_leg: { type: 'string', required: true, oneOf: TRIP_LEGS },
      result_count: { type: 'number', required: true },
      // Only present when result_count is 0.
      search_status: { type: 'string', oneOf: ['no_results'] },
      message: { type: 'string' },
      items: {
        type: 'object[]',
        required: true,
        shape: {
          ...LIST_ITEM_SHAPE,
          arrival_time: { type: 'string', required: true, nullable: true },
          duration: { type: 'string', required: true, nullable: true },
          boarding_point: { type: 'string', required: true, nullable: true },
          dropping_point: { type: 'string', required: true, nullable: true }
        }
      }
    }
  },

  // ----------------------------------------------------------------- selection

  seat_select: {
    description: 'The seat-selection dialog is opened for a bus or flight.',
    strictKeys: true,
    fields: LEGACY_INVENTORY_TAG
  },

  view_bus_detail: {
    description: 'The seat-selection dialog renders the chosen bus.',
    strictKeys: true,
    fields: {
      ...JOURNEY_FIELDS,
      ...USER_FIELDS,
      ...PRICING_FIELDS,
      item_list_name: { type: 'string', required: true, nullable: true },
      trip_leg: { type: 'string', required: true, oneOf: TRIP_LEGS },
      bus_operator: { type: 'string', required: true },
      item_id: { type: 'string', required: true, nullable: true },
      item_name: { type: 'string', required: true },
      departure_time: { type: 'string', required: true, nullable: true },
      arrival_time: { type: 'string', required: true, nullable: true },
      duration: { type: 'string', required: true, nullable: true },
      seats_available: { type: 'number', required: true },
      seat_types_available: { type: 'string[]', required: true },
      route: { type: 'string', required: true },
      boarding_point: { type: 'string', required: true, nullable: true },
      dropping_point: { type: 'string', required: true, nullable: true }
    }
  },

  seat_window_loaded: {
    description: 'The seat map finishes loading and prices are known.',
    strictKeys: true,
    fields: {
      ...JOURNEY_FIELDS,
      ...USER_FIELDS,
      trip_leg: { type: 'string', required: true, oneOf: TRIP_LEGS },
      bus_operator: { type: 'string', required: true },
      item_id: { type: 'string', required: true, nullable: true },
      item_name: { type: 'string', required: true },
      departure_time: { type: 'string', required: true, nullable: true },
      arrival_time: { type: 'string', required: true, nullable: true },
      duration: { type: 'string', required: true, nullable: true },
      seats_available: { type: 'number', required: true },
      seats_total: { type: 'number', required: true },
      seat_types_available: { type: 'string[]', required: true },
      pricing: { type: 'object', required: true },
      route: { type: 'string', required: true },
      boarding_point: { type: 'string', required: true, nullable: true },
      dropping_point: { type: 'string', required: true, nullable: true }
    }
  },

  continue_booking: {
    description: 'User confirms seats and proceeds from the seat dialog.',
    strictKeys: true,
    fields: LEGACY_INVENTORY_TAG
  },

  continue_booking_roundtrip: {
    description: 'User confirms both legs of a round-trip flight.',
    strictKeys: true,
    fields: {
      time: { type: 'number', required: true },
      data: {
        type: 'object',
        required: true,
        shape: {
          fromCity: { type: 'string', required: true },
          toCity: { type: 'string', required: true },
          outboundOperator: { type: 'string', required: true },
          inboundOperator: { type: 'string', required: true },
          totalPrice: { type: 'number', required: true }
        }
      }
    }
  },

  begin_checkout: {
    description: 'Seats are confirmed and the user moves to the booking form.',
    strictKeys: true,
    fields: {
      ...JOURNEY_FIELDS,
      ...USER_FIELDS,
      ...PRICING_FIELDS,
      item_list_name: { type: 'string', required: true, nullable: true },
      trip_leg: { type: 'string', required: true, oneOf: TRIP_LEGS },
      bus_operator: { type: 'string', required: true },
      departure_time: { type: 'string', required: true, nullable: true },
      seats_available: { type: 'number', required: true },
      seats_total: { type: 'number', required: true },
      route: { type: 'string', required: true },
      selected_item: { type: 'object', required: true, shape: SELECTED_ITEM_SHAPE },
      value: { type: 'number', required: true },
      currency: { type: 'string', required: true }
    }
  },

  // ------------------------------------------------------------------ checkout

  add_pass_info: {
    description: 'Passenger details are filled in on the confirmation page.',
    strictKeys: true,
    fields: {
      ...JOURNEY_FIELDS,
      trip_leg: { type: 'string', required: true, oneOf: TRIP_LEGS },
      all_fields_valid: { type: 'boolean', required: true },
      terms_accepted: { type: 'boolean', required: true },
      passenger_info: {
        type: 'object[]',
        required: true,
        shape: {
          passenger_index: { type: 'number', required: true },
          full_name: { type: 'string', required: true, nullable: true },
          gender: { type: 'string', required: true, nullable: true },
          location: { type: 'string', required: true, nullable: true },
          age: { type: 'number', required: true, nullable: true }
        }
      }
    }
  },

  add_payment_info: {
    description: 'User picks a payment method on the confirmation page.',
    strictKeys: true,
    fields: {
      ...JOURNEY_FIELDS,
      trip_leg: { type: 'string', required: true, oneOf: TRIP_LEGS },
      payment_type: { type: 'string', required: true },
      currency: { type: 'string', required: true },
      base_fare: { type: 'number', required: true },
      convenience_fee: { type: 'number', required: true },
      discount: { type: 'number', required: true },
      final_price: { type: 'number', required: true },
      value: { type: 'number', required: true },
      terms_accepted: { type: 'boolean', required: true },
      promo_applied: { type: 'boolean', required: true },
      coupon: { type: 'string', required: true, nullable: true }
    }
  },

  apply_promotion: {
    description: 'A coupon or voucher is applied to the booking total.',
    strictKeys: true,
    fields: {
      promotion_name: { type: 'string', required: true, nullable: true },
      trip_type: { type: 'string', required: true, oneOf: TRIP_TYPES },
      currency: { type: 'string', required: true },
      base_fare: { type: 'number', required: true },
      convenience_fee: { type: 'number', required: true },
      discount: { type: 'number', required: true },
      final_price: { type: 'number', required: true },
      value_before_discount: { type: 'number', required: true },
      value_after_discount: { type: 'number', required: true },
      promo_applied: { type: 'boolean', required: true }
    }
  },

  payment_initiated: {
    description: 'User submits the booking and payment is handed to the gateway.',
    strictKeys: true,
    fields: {
      ...JOURNEY_FIELDS,
      ...USER_FIELDS,
      ...PRICING_FIELDS,
      item_list_name: { type: 'string', required: true },
      trip_leg: { type: 'string', required: true, oneOf: TRIP_LEGS },
      items: { type: 'object[]', required: true, shape: LIST_ITEM_SHAPE },
      bus_operator: { type: 'string', required: true },
      departure_time: { type: 'string', required: true, nullable: true },
      seats_available: { type: 'number', required: true },
      seats_total: { type: 'number', required: true },
      route: { type: 'string', required: true },
      selected_item: { type: 'object', required: true, shape: SELECTED_ITEM_SHAPE },
      passenger_info: { type: 'object[]', required: true },
      payment_type: { type: 'string', required: true },
      value: { type: 'number', required: true },
      currency: { type: 'string', required: true },
      promo_applied: { type: 'boolean', required: true },
      coupon: { type: 'string', required: true, nullable: true },
      terms_accepted: { type: 'boolean', required: true },
      all_fields_valid: { type: 'boolean', required: true },
      checkout_ready_time_ms: { type: 'number', required: true }
    }
  },

  initiate_checkout: {
    description: 'Flights checkout is submitted to the payment gateway.',
    strictKeys: true,
    fields: {
      time: { type: 'number', required: true },
      data: {
        type: 'object',
        required: true,
        shape: {
          customerName: { type: 'string', required: true, nullable: true },
          customerEmail: { type: 'string', required: true, nullable: true },
          customerPhone: { type: 'string', required: true, nullable: true },
          fromCity: { type: 'string', required: true },
          toCity: { type: 'string', required: true },
          gender: { type: 'string', required: true, nullable: true }
        }
      }
    }
  },

  // ------------------------------------------------------------------ purchase

  purchase: {
    description: 'Payment succeeded. One event per trip leg. Drives GA4 revenue.',
    variants: [
      {
        // The flights funnel emits the same event name as a legacy GTM tag.
        name: 'flights legacy tag',
        strictKeys: true,
        fields: {
          time: { type: 'number', required: true },
          data: { type: 'object', required: true }
        }
      },
      {
        // The Universal Analytics enhanced-ecommerce block pushed alongside it.
        name: 'enhanced ecommerce',
        strictKeys: true,
        fields: {
          ecommerce: { type: 'object', required: true }
        }
      }
    ],
    fields: {
      transaction_id: { type: 'string', required: true },
      ...JOURNEY_FIELDS,
      ...USER_FIELDS,
      trip_leg: { type: 'string', required: true, oneOf: TRIP_LEGS },
      bus_operator: { type: 'string', required: true },
      departure_time: { type: 'string', required: true, nullable: true },
      route: { type: 'string', required: true },
      boarding_point: { type: 'string', required: true, nullable: true },
      dropping_point: { type: 'string', required: true, nullable: true },
      items: {
        type: 'object[]',
        required: true,
        shape: {
          item_id: { type: 'string', required: true },
          item_name: { type: 'string', required: true },
          bus_operator: { type: 'string', required: true },
          seat_type: { type: 'string', required: true, nullable: true },
          quantity: { type: 'number', required: true },
          price: { type: 'number', required: true },
          route: { type: 'string', required: true }
        }
      },
      passenger_info: { type: 'object[]', required: true, shape: PASSENGER_INFO_SHAPE },
      payment_type: { type: 'string', required: true, nullable: true },
      value: { type: 'number', required: true },
      currency: { type: 'string', required: true },
      coupon: { type: 'string', required: true, nullable: true },
      promo_applied: { type: 'boolean', required: true },
      // Universal Analytics enhanced-ecommerce block, still consumed by GTM tags.
      ecommerce: { type: 'object', required: true }
    }
  },

  checkout: {
    description: 'Universal Analytics enhanced-ecommerce checkout step (flights).',
    strictKeys: true,
    fields: {
      ecommerce: { type: 'object', required: true }
    }
  },

  // -------------------------------------------------------------------- other

  contact_click: {
    description: 'User clicks an operator contact detail on the contact page.',
    strictKeys: true,
    fields: {
      time: { type: 'number', required: true },
      type: { type: 'object', required: true, nullable: true }
    }
  },

  detail: {
    description: 'Universal Analytics enhanced-ecommerce product detail view.',
    strictKeys: true,
    fields: {
      ecommerce: { type: 'object', required: true }
    }
  }
};

/**
 * Contact-page tracking builds its event name from a `type` argument
 * (`${type}_click`). Anything matching this pattern validates against the
 * `contact_click` spec rather than needing its own declaration.
 */
const DYNAMIC_EVENT_ALIASES: Array<{ pattern: RegExp; specName: string }> = [
  { pattern: /^[a-z0-9_]+_click$/, specName: 'contact_click' }
];

export function resolveEventSpec(eventName: string): AnalyticsEventSpec | undefined {
  if (ANALYTICS_EVENT_SCHEMA[eventName]) {
    return ANALYTICS_EVENT_SCHEMA[eventName];
  }

  const alias = DYNAMIC_EVENT_ALIASES.find(entry => entry.pattern.test(eventName));
  return alias ? ANALYTICS_EVENT_SCHEMA[alias.specName] : undefined;
}

export function isKnownEvent(eventName: string): boolean {
  return !!resolveEventSpec(eventName);
}

function typeOfValue(value: any): AnalyticsFieldType | 'null' | 'undefined' | 'unknown' {
  if (value === null) { return 'null'; }
  if (value === undefined) { return 'undefined'; }
  if (Array.isArray(value)) {
    return value.every(entry => typeof entry === 'string') && value.length ? 'string[]' : 'object[]';
  }
  if (typeof value === 'string') { return 'string'; }
  if (typeof value === 'number') { return 'number'; }
  if (typeof value === 'boolean') { return 'boolean'; }
  if (typeof value === 'object') { return 'object'; }
  return 'unknown';
}

function matchesType(value: any, type: AnalyticsFieldType): boolean {
  switch (type) {
    case 'string': return typeof value === 'string';
    // NaN would silently zero out GA4 revenue, so reject it explicitly.
    case 'number': return typeof value === 'number' && !isNaN(value);
    case 'boolean': return typeof value === 'boolean';
    case 'string[]': return Array.isArray(value) && value.every(entry => typeof entry === 'string');
    case 'object[]': return Array.isArray(value) && value.every(entry => !!entry && typeof entry === 'object' && !Array.isArray(entry));
    case 'object': return !!value && typeof value === 'object' && !Array.isArray(value);
    default: return false;
  }
}

function validateFields(
  value: any,
  fields: AnalyticsFieldSpecMap,
  path: string,
  strictKeys: boolean,
  errors: string[]
): void {
  Object.keys(fields).forEach(field => {
    const spec = fields[field];
    const fieldPath = path ? `${path}.${field}` : field;
    const present = value && Object.prototype.hasOwnProperty.call(value, field);
    const fieldValue = present ? value[field] : undefined;

    if (!present || fieldValue === undefined) {
      if (spec.required) {
        errors.push(`${fieldPath} is required but missing`);
      }
      return;
    }

    if (fieldValue === null) {
      if (!spec.nullable) {
        errors.push(`${fieldPath} is null but is not declared nullable`);
      }
      return;
    }

    if (!matchesType(fieldValue, spec.type)) {
      errors.push(`${fieldPath} should be ${spec.type} but got ${typeOfValue(fieldValue)}`);
      return;
    }

    if (spec.oneOf && spec.oneOf.indexOf(fieldValue) === -1) {
      errors.push(`${fieldPath} should be one of [${spec.oneOf.join(', ')}] but got ${JSON.stringify(fieldValue)}`);
      return;
    }

    if (spec.shape) {
      if (spec.type === 'object') {
        validateFields(fieldValue, spec.shape, fieldPath, strictKeys, errors);
      }
      if (spec.type === 'object[]') {
        (fieldValue as any[]).forEach((entry, index) => {
          validateFields(entry, spec.shape, `${fieldPath}[${index}]`, strictKeys, errors);
        });
      }
    }
  });

  if (strictKeys && value) {
    Object.keys(value).forEach(key => {
      if (key !== 'event' && !fields[key]) {
        errors.push(`${path ? `${path}.` : ''}${key} is not declared in the schema`);
      }
    });
  }
}

export interface AnalyticsValidationResult {
  valid: boolean;
  errors: string[];
}

/**
 * Validate a fully-formed dataLayer payload (including its `event` key).
 */
export function validateAnalyticsEvent(payload: any): AnalyticsValidationResult {
  const errors: string[] = [];

  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    return { valid: false, errors: ['payload must be a plain object'] };
  }

  const eventName = payload.event;

  if (typeof eventName !== 'string' || !eventName) {
    return { valid: false, errors: ['payload.event must be a non-empty string'] };
  }

  const spec = resolveEventSpec(eventName);

  if (!spec) {
    return {
      valid: false,
      errors: [`"${eventName}" is not declared in ANALYTICS_EVENT_SCHEMA — add it there before pushing it`]
    };
  }

  validateFields(payload, spec.fields, '', !!spec.strictKeys, errors);

  if (!errors.length || !spec.variants?.length) {
    return { valid: !errors.length, errors };
  }

  // Report the variant that came closest, so the failure names one concrete
  // shape rather than every shape at once.
  let best: { name: string | null; errors: string[] } = { name: null, errors };

  for (const variant of spec.variants) {
    const variantErrors: string[] = [];
    validateFields(payload, variant.fields, '', !!variant.strictKeys, variantErrors);

    if (!variantErrors.length) {
      return { valid: true, errors: [] };
    }

    if (variantErrors.length < best.errors.length) {
      best = { name: variant.name, errors: variantErrors };
    }
  }

  return {
    valid: false,
    errors: best.name
      ? best.errors.map(error => `[${best.name} shape] ${error}`)
      : best.errors
  };
}

export function formatValidationErrors(eventName: string, errors: string[]): string {
  return `Invalid analytics payload for "${eventName}":\n  - ${errors.join('\n  - ')}`;
}
